/**
 * ActionMenu - Kebab-trigger action menu that portals to document.body so it
 * can escape ancestor `overflow` clipping (e.g. tables wrapped in
 * `overflow-x-auto`).
 *
 * The menu is positioned with `position: fixed`, anchored to the trigger via
 * `getBoundingClientRect()`, and flips above the trigger if it would overflow
 * the viewport bottom. It closes on outside click, Escape, scroll, and resize.
 */
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export type ActionMenuItem = {
	label: string;
	onClick: () => void;
	variant?: 'default' | 'danger';
	icon?: React.ReactNode;
	disabled?: boolean;
};

export type ActionMenuProps = {
	/** Accessible label for the trigger button (e.g. "Actions for work order 123") */
	ariaLabel: string;
	/** Menu items. Falsy entries are filtered so callers can write `cond && {...}` */
	items: (ActionMenuItem | false | null | undefined)[];
	/** Horizontal alignment relative to trigger. Default 'end' (right-aligned). */
	align?: 'start' | 'end';
	/** Optional extra classes merged onto the trigger button. */
	className?: string;
};

type MenuPosition = {
	top: number;
	left?: number;
	right?: number;
	/** False on first render so the menu is invisible while we measure & flip */
	measured: boolean;
};

const isMenuItem = (item: ActionMenuItem | false | null | undefined): item is ActionMenuItem => Boolean(item);

export const ActionMenu: React.FC<ActionMenuProps> = ({ ariaLabel, items, align = 'end', className = '' }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [position, setPosition] = useState<MenuPosition | null>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const visibleItems = items.filter(isMenuItem);

	const openMenu = useCallback(() => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		const top = rect.bottom + 4;
		setPosition(
			align === 'end'
				? { top, right: window.innerWidth - rect.right, measured: false }
				: { top, left: rect.left, measured: false }
		);
		setIsOpen(true);
	}, [align]);

	const closeMenu = useCallback((returnFocus = false) => {
		setIsOpen(false);
		setPosition(null);
		if (returnFocus) triggerRef.current?.focus();
	}, []);

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isOpen) {
			closeMenu();
		} else {
			openMenu();
		}
	};

	/* After the portal mounts, measure the menu and flip above the trigger if it
	 * would overflow the viewport bottom. Runs synchronously before paint so the
	 * user never sees the unflipped position. */
	useLayoutEffect(() => {
		if (!isOpen || !position || position.measured) return;
		if (!menuRef.current || !triggerRef.current) return;
		const menuHeight = menuRef.current.offsetHeight;
		const triggerRect = triggerRef.current.getBoundingClientRect();
		const wouldOverflow = position.top + menuHeight > window.innerHeight;
		setPosition(
			wouldOverflow
				? { ...position, top: triggerRect.top - menuHeight - 4, measured: true }
				: { ...position, measured: true }
		);
	}, [isOpen, position]);

	/* Close on outside click, Escape, scroll (capture-phase to catch inner
	 * scrolls like overflow-x-auto), and resize. */
	useEffect(() => {
		if (!isOpen) return;

		const handleMouseDown = (e: MouseEvent) => {
			const target = e.target as Node;
			if (triggerRef.current?.contains(target)) return;
			if (menuRef.current?.contains(target)) return;
			closeMenu();
		};

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeMenu(true);
		};

		const handleScrollOrResize = () => closeMenu();

		document.addEventListener('mousedown', handleMouseDown);
		document.addEventListener('keydown', handleKeyDown);
		window.addEventListener('scroll', handleScrollOrResize, true);
		window.addEventListener('resize', handleScrollOrResize);

		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('scroll', handleScrollOrResize, true);
			window.removeEventListener('resize', handleScrollOrResize);
		};
	}, [isOpen, closeMenu]);

	const handleItemClick = (item: ActionMenuItem) => {
		if (item.disabled) return;
		item.onClick();
		closeMenu();
	};

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				onClick={handleToggle}
				/* 44px square on touch viewports, shrinking to the dense 32px only
				   on desktop. The trigger is the sole way into row actions on a
				   list, and at 32px it was the smallest target in the app. */
				className={`action-menu w-44px h-44px desktop:w-32px desktop:h-32px rounded-6px flex items-center justify-center text-brand-text-muted hover:bg-brand-surface transition-colors ${className}`}
				data-ref="atom-action-menu"
				aria-label={ariaLabel}
				aria-haspopup="menu"
				aria-expanded={isOpen}
				title="Actions"
			>
				<svg className="w-16px h-16px" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
					<path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM11.5 15.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z" />
				</svg>
			</button>

			{isOpen && position && createPortal(
				<div
					ref={menuRef}
					data-action-menu="true"
					role="menu"
					style={{
						position: 'fixed',
						top: position.top,
						left: position.left,
						right: position.right,
						visibility: position.measured ? 'visible' : 'hidden',
					}}
					/* Sized to its content rather than a fixed 136px: the detail
					   pages now push their longer, lower-frequency actions in
					   here ("Create Follow-Up (no failed items)"), and at 136px
					   those wrapped onto three cramped lines. Capped against the
					   viewport so it can never overflow a phone screen. */
					className="action-menu__menu w-max min-w-[160px] max-w-[calc(100vw-32px)] bg-white border border-brand-border rounded-8px shadow-lg z-50 py-4px"
				>
					{visibleItems.map((item, idx) => {
						const variantClasses = item.variant === 'danger'
							? 'text-red-600 hover:bg-red-50'
							: 'text-brand-text hover:bg-brand-surface';
						const stateClasses = item.disabled ? 'opacity-50 cursor-not-allowed' : '';
						const layoutClasses = item.icon ? 'flex items-center gap-8px' : '';
						return (
							<button
								key={idx}
								type="button"
								role="menuitem"
								onClick={() => handleItemClick(item)}
								disabled={item.disabled}
								/* min-h-44px on touch keeps each row a full tap target —
							   the menu items are the actions the trigger opens, so
							   an easy-to-hit trigger with 33px rows behind it just
							   moves the mis-tap one step later. */
							className={['action-menu__item', item.disabled ? 'action-menu__item--disabled' : '', 'w-full text-left px-12px py-8px text-14px transition-colors min-h-44px desktop:min-h-0 flex items-center', layoutClasses, variantClasses, stateClasses].filter(Boolean).join(' ')}
							>
								{item.icon && <span className="action-menu__icon flex-shrink-0">{item.icon}</span>}
								<span className="action-menu__label">{item.label}</span>
							</button>
						);
					})}
				</div>,
				document.body
			)}
		</>
	);
};

export default ActionMenu;
